import type { CSSProperties } from 'react'
import { getContrastTextColor, hexToHsl } from '@structural-climbing/ui'

/** What the member app falls back to when a gym has not picked a colour. */
export const DEFAULT_PRIMARY = '#BD4627'
export const DEFAULT_SECONDARY = '#EEE7DD'

/**
 * The same light tints the member app's useGymTheme emits as --gym-wash,
 * --gym-veil and --gym-line: the secondary colour's hue held very light, with
 * its saturation pulled down.
 */
export function tint(hex: string, lightness: number, saturationFactor: number): string {
  const [h, s] = hexToHsl(hex).split(' ')
  return `hsl(${h} ${(parseFloat(s) * saturationFactor).toFixed(1)}% ${lightness}%)`
}

/** The primary's hue, darkened until it reads as text on near-white paper. */
function inkFor(hex: string): string {
  const [h, s, l] = hexToHsl(hex).split(' ')
  return `hsl(${h} ${Math.min(parseFloat(s), 70)}% ${Math.min(parseFloat(l), 42)}%)`
}

function luminance(hex: string): number {
  const c = [1, 3, 5].map((i) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]
}

/**
 * Too pale to see as a thin line on white (the tab underline, the outline of
 * the selected gym). Text on the colour is always made readable by picking
 * black or white, so this is the risk worth warning about.
 */
export function isTooPale(hex: string): boolean {
  const ratio = (1 + 0.05) / (luminance(hex) + 0.05)
  return ratio < 1.6
}

/**
 * CSS variables for the drawn member screens, scoped to the preview so the gym
 * app's own colours never change.
 */
export function brandStyle(primary: string | null, secondary: string | null): CSSProperties {
  const accent = primary ?? DEFAULT_PRIMARY
  const tintSource = secondary ?? accent
  return {
    '--m-brand': accent,
    '--m-on-brand': getContrastTextColor(accent),
    '--m-ink': inkFor(accent),
    '--m-wash': tint(tintSource, 97, 0.45),
    '--m-veil': tint(tintSource, 94, 0.4),
    '--m-line': tint(tintSource, 91, 0.35),
  } as CSSProperties
}
